import { useCustom, useCustomMutation, useGetLocale } from '@refinedev/core';
import { UsageEventTypes } from '@cc/schema';
import type { FeatureAnnouncement } from '../../schema/be-graphql-generated';
import { appendPortalUsageEvent } from '../../providers/usageEvents';

/**
 * E32 · the portal's side of What's New — cc-account chapter "Client Runtime — What's New (portal,
 * E32)": PortalFeatureAnnouncements (buildOrder 10), AcknowledgeFeatureAnnouncement (11), the
 * article-opened usage emit (12), PortalFeatureAnnouncementHistory (13). One hook for the banner
 * and the drawer, over refine's `useCustom` like every other portal reader (the Apollo hooks in
 * the generated file are the mobile app's shape; the portal speaks through `gqlDataProvider`).
 */
export const WHATS_NEW_PORTAL_SURFACE = 'portal';

export const FEATURE_ANNOUNCEMENT_FIELDS = [
  'announcementId',
  'variantId',
  'title',
  'summary',
  'mediaUrl',
  'articleUrl',
  'surface',
  'language',
  'relatedReleases',
  'publishAt',
  'acknowledgedAt',
] as const;

const variablesFor = (language: string) => ({
  surface: {
    name: 'surface',
    type: 'String!',
    value: WHATS_NEW_PORTAL_SURFACE,
  },
  language: { name: 'language', type: 'String', value: language },
});

export const usePortalLanguage = (): string => {
  const locale = useGetLocale();
  return (locale() ?? 'en').toLowerCase().split('-')[0];
};

/** The unacknowledged announcements for this therapist on the portal, newest first. */
export const usePendingWhatsNew = (enabled = true) => {
  const language = usePortalLanguage();
  const query = useCustom<FeatureAnnouncement[]>({
    url: '',
    method: 'get',
    meta: {
      operation: 'pendingFeatureAnnouncements',
      fields: [...FEATURE_ANNOUNCEMENT_FIELDS],
      variables: variablesFor(language),
    },
    queryOptions: { enabled },
    errorNotification: false,
  });
  return { ...query, rows: query.data?.data ?? [], language };
};

/** Everything that was ever this therapist's, one row per announcement, newest first. */
export const useWhatsNewHistory = (enabled: boolean) => {
  const language = usePortalLanguage();
  const query = useCustom<FeatureAnnouncement[]>({
    url: '',
    method: 'get',
    meta: {
      operation: 'featureAnnouncementHistory',
      fields: [...FEATURE_ANNOUNCEMENT_FIELDS],
      variables: variablesFor(language),
    },
    queryOptions: { enabled },
    errorNotification: false,
  });
  return { ...query, rows: query.data?.data ?? [] };
};

/** The dismissal — against the ANNOUNCEMENT, with the variant that was on the screen as `seenAs`. */
export const useAcknowledgeWhatsNew = () => {
  const { mutateAsync, isLoading } = useCustomMutation();
  const acknowledge = (
    row: Pick<FeatureAnnouncement, 'announcementId' | 'variantId'>,
  ) =>
    mutateAsync({
      url: '',
      method: 'post',
      values: {},
      errorNotification: false,
      meta: {
        operation: 'acknowledgeFeatureAnnouncement',
        variables: {
          announcementId: {
            name: 'announcementId',
            type: 'ID!',
            value: row.announcementId,
          },
          seenAs: { name: 'seenAs', type: 'ID!', value: row.variantId },
          surface: {
            name: 'surface',
            type: 'String!',
            value: WHATS_NEW_PORTAL_SURFACE,
          },
        },
      },
    });
  return { acknowledge, isLoading };
};

/**
 * E32 · RecordUsageEvent · portal-whats-new-article-opened — the portal's one telemetry signal of
 * this chapter: `featureAnnouncement/<announcementId>` as the focus (the announcement is the
 * domain noun, the `portalBuild` precedent), `extras {surface: portal}`. Telemetry never fails the
 * surface it rides on (`appendPortalUsageEvent` reports to Sentry and swallows).
 */
export const openWhatsNewArticle = (
  row: Pick<FeatureAnnouncement, 'announcementId' | 'articleUrl'>,
): void => {
  if (!row.articleUrl) return;
  void appendPortalUsageEvent(
    UsageEventTypes.FeatureAnnouncementArticleOpened,
    `featureAnnouncement/${row.announcementId}`,
    { surface: WHATS_NEW_PORTAL_SURFACE },
  );
  window.open(row.articleUrl, '_blank', 'noopener,noreferrer');
};
